import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Radar, Crosshair, Bell, CreditCard, ShieldCheck, Zap, Globe, Check, ArrowRight,
  Quote, Cpu, MonitorDown, Tag, Sparkles, Lock, KeyRound, Home as HomeIcon, Download,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { authApi } from "@/lib/auth";

function FeatureCard({ icon: Icon, title, children, accent = "#EAB308" }) {
  return (
    <div className="group bg-[#0d0d0d] border border-[#262626] p-6 rounded-sm hover:border-[#EAB308]/40 transition-all">
      <div
        className="w-10 h-10 border flex items-center justify-center mb-4 transition-all group-hover:scale-110"
        style={{ borderColor: `${accent}55`, color: accent, backgroundColor: `${accent}10` }}
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-2">{title}</h3>
      <p className="text-sm text-[#A3A3A3] leading-relaxed">{children}</p>
    </div>
  );
}

function SecurityCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm hover:border-[#22C55E]/40 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 border border-[#22C55E]/40 bg-[#22C55E]/10 flex items-center justify-center rounded-sm">
          <Icon className="w-4 h-4 text-[#22C55E]" strokeWidth={2.2} />
        </div>
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider">{title}</h3>
      </div>
      <p className="text-xs text-[#A3A3A3] leading-relaxed">{body}</p>
    </div>
  );
}

function RetailerPill({ name, color }) {
  return (
    <span
      className="text-[11px] font-mono font-bold px-2.5 py-1 border rounded-sm uppercase tracking-wider"
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}08` }}
    >
      {name}
    </span>
  );
}

const RETAILERS = [
  { name: "Target", color: "#CC0000" },
  { name: "Walmart", color: "#0071CE" },
  { name: "Best Buy", color: "#0046BE" },
  { name: "Amazon", color: "#FF9900" },
  { name: "GameStop", color: "#E51F2A" },
  { name: "TCGplayer", color: "#F4B223" },
  { name: "eBay", color: "#E53238" },
  { name: "Pokemon Center", color: "#FFCB05" },
  { name: "JB Hi-Fi", color: "#FFF000" },
  { name: "Kmart AU", color: "#E60028" },
  { name: "Target AU", color: "#E4002B" },
  { name: "BIG W", color: "#0099D8" },
  { name: "EB Games AU", color: "#ED1C24" },
  { name: "Zing Pop", color: "#FF6B00" },
  { name: "Game Traders", color: "#1E88E5" },
];

const TESTIMONIALS = [
  {
    quote: "Got 4 Surging Sparks ETBs from Kmart AU before they hit Twitter. The 1-click cart link is gold.",
    name: "Brennan T.",
    role: "Sydney · Reseller",
  },
  {
    quote: "I sleep through every drop now. Wake up, check alerts, hit 'open cart'. That's it.",
    name: "Marcus K.",
    role: "Melbourne · Collector",
  },
  {
    quote: "The deal score saved me from overpaying $40 on a Crown Zenith UPC. CardRadar paid for itself in week one.",
    name: "Lina R.",
    role: "Brisbane · Pokemon TCG",
  },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Pick your targets", body: "Filter by game, set, retailer, region, max price and deal score. Save as watches." },
  { step: "02", title: "Arm the scanner", body: "CardRadar polls every retailer in your filter set on a tight interval. Australia and US covered." },
  { step: "03", title: "Get alerted in real time", body: "Dashboard pings + Discord/Telegram push the second something matches." },
  { step: "04", title: "1-click cart", body: "Open the retailer cart instantly, or arm the bot to attempt auto-checkout." },
];

function MockDashboard() {
  // visual fake dashboard for the hero
  return (
    <div className="bg-[#0d0d0d] border border-[#262626] rounded-sm overflow-hidden shadow-[0_0_60px_rgba(234,179,8,0.06)]">
      <div className="bg-[#050505] border-b border-[#262626] px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="pulse-dot" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A3A3A3] font-mono">Scanning · 3.2s avg</span>
        </div>
        <span className="text-[10px] font-mono text-[#737373]">last scan: just now</span>
      </div>
      <div className="grid grid-cols-3 gap-px bg-[#262626]">
        {[
          { title: "Surging Sparks ETB", retailer: "Kmart AU", color: "#E60028", price: "A$84.98", deal: 82, status: "in_stock" },
          { title: "Stellar Crown Booster Box", retailer: "JB Hi-Fi", color: "#FFF000", price: "A$219.00", deal: 71, status: "in_stock" },
          { title: "151 Mini Tin", retailer: "Target AU", color: "#E4002B", price: "A$24.50", deal: 64, status: "in_stock" },
        ].map((c, i) => (
          <div key={i} className="bg-[#0d0d0d] p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <RetailerPill name={c.retailer} color={c.color} />
              <span className="bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 text-[9px] font-mono px-1.5 py-0.5 rounded-sm uppercase font-bold">IN STOCK</span>
            </div>
            <div className="text-[12px] text-white font-semibold leading-tight">{c.title}</div>
            <div className="flex items-end justify-between">
              <span className="font-mono text-lg font-bold text-white">{c.price}</span>
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 border rounded-sm text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10">
                DEAL {c.deal}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-[#0a0a0a] border-t border-[#262626] px-3 py-2 flex items-center justify-between text-[10px] font-mono text-[#737373]">
        <span>← 437 listings tracked · 142 in stock · 38 hot deals</span>
        <span className="text-[#EAB308]">→ Live alerts streaming</span>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    authApi.get("/billing/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const ctaTarget = user ? (user.subscription_status === "active" ? "/app" : "/billing") : "/register";

  return (
    <div className="min-h-screen bg-[#050505] text-white grain overflow-x-hidden">
      {/* Nav */}
      <nav className="border-b border-[#262626] sticky top-0 z-40 bg-[#050505]/90 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <div className="relative w-8 h-8 border border-[#EAB308] flex items-center justify-center bg-black">
              <Radar className="w-4 h-4 text-[#EAB308]" strokeWidth={2.5} />
              <div className="scanline" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-base font-black tracking-tighter uppercase">
                Card<span className="text-[#EAB308]">Radar</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <a href="#features" className="hidden md:inline text-xs uppercase tracking-wider font-semibold text-[#A3A3A3] hover:text-white px-3 py-2">Features</a>
            <a href="#how" className="hidden md:inline text-xs uppercase tracking-wider font-semibold text-[#A3A3A3] hover:text-white px-3 py-2">How it works</a>
            <a href="#pricing" className="hidden md:inline text-xs uppercase tracking-wider font-semibold text-[#A3A3A3] hover:text-white px-3 py-2">Pricing</a>
            {user ? (
              <Link to="/app" data-testid="nav-go-app" className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#262626] hover:border-[#EAB308] text-white">
                Open app →
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#262626] hover:border-[#A3A3A3] text-white">
                  Sign in
                </Link>
                <Link to="/register" data-testid="nav-get-started" className="px-3 py-2 text-xs uppercase tracking-wider font-bold bg-[#EAB308] text-black hover:bg-[#FDE047]">
                  Start free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-2.5 py-1 rounded-sm mb-6">
              <Crosshair className="w-3 h-3" />
              Auto-buy · 15 retailers · sub-second checkout
            </div>
            <h1 className="font-heading text-5xl md:text-6xl xl:text-7xl font-black tracking-tighter uppercase leading-[0.95]">
              Snipe the drop.<br />
              <span className="text-[#EAB308] accent-glow">Beat the bots.</span><br />
              <span className="text-white">Stock the shelf.</span>
            </h1>
            <p className="text-base md:text-lg text-[#A3A3A3] mt-6 max-w-xl leading-relaxed">
              CardRadar tracks every major TCG (Pokémon, Magic, Yu-Gi-Oh!, One Piece, Lorcana, Digimon, Flesh &amp; Blood) across <span className="text-white font-semibold">15 retailers</span> in AU and US. Set your targets, pick your budget, and let the bot do the rest. <span className="text-white font-semibold">Fast, hands-off, built for the drop.</span>
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to={ctaTarget}
                data-testid="hero-cta"
                className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center gap-2 shadow-[0_0_24px_rgba(234,179,8,0.25)]"
              >
                <Zap className="w-4 h-4" />
                {user ? "Open dashboard" : "Arm the bot"}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how"
                className="border border-[#262626] hover:border-[#A3A3A3] text-white text-sm font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm flex items-center gap-2 transition-all"
              >
                See how it works
              </a>
            </div>
            <div className="flex items-center gap-4 mt-6 text-[11px] font-mono text-[#737373]">
              <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#22C55E]" /> Cancel anytime</div>
              <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#22C55E]" /> AU + US retailers</div>
              <div className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-[#EAB308]" /> Cards stay on your machine</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <MockDashboard />
          </div>
        </div>
        {/* Retailer marquee */}
        <div className="mt-16 border-y border-[#262626] py-4">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#737373] mb-3 font-mono">Monitored retailers</div>
          <div className="flex flex-wrap gap-2" data-testid="retailers-marquee">
            {RETAILERS.map((r) => <RetailerPill key={r.name} {...r} />)}
          </div>
        </div>
      </section>

      {/* Security differentiator: the moat */}
      <section className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 border-y border-[#262626]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#EAB308] font-mono mb-3">// bonus, built right</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase leading-[0.95]">
              Your cards. <span className="text-[#EAB308]">Your machine.</span>
            </h2>
            <p className="text-[#A3A3A3] text-base mt-5 leading-relaxed">
              Bonus from the dual-system design: all the sensitive stuff (store logins, card numbers, shipping addresses, proxies) lives on your computer, encrypted with AES-128 in a local vault. The cloud has zero ability to read them.
            </p>
            <p className="text-[#A3A3A3] text-base mt-4 leading-relaxed">
              When (not if) competing bots get breached, your data isn&apos;t in their dump. <strong className="text-white">Because it was never there to begin with.</strong> Subscribe with confidence.
            </p>
            <Link
              to={user ? "/app/desktop" : "/register"}
              className="inline-flex items-center gap-2 mt-6 bg-[#EAB308] text-black font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-sm hover:bg-[#FDE047] transition-all"
            >
              <Download className="w-4 h-4" />
              {user ? "Install the desktop app" : "Get the desktop app"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SecurityCard
              icon={Lock}
              title="Cards stay local"
              body="Saved in your Fernet-encrypted SQLite vault. The cloud has zero API endpoints to read or write card data."
            />
            <SecurityCard
              icon={KeyRound}
              title="Logins stay local"
              body="TCGplayer / Pokemon Center / JB Hi-Fi credentials live in your machine's vault. If you change your password, the cloud never knew the old one."
            />
            <SecurityCard
              icon={HomeIcon}
              title="Addresses stay local"
              body="Shipping profiles never POST to our servers. Mail-forwarder addresses, sneaker-farm routes, all yours, none ours."
            />
            <SecurityCard
              icon={ShieldCheck}
              title="Zero PCI exposure"
              body="Because we never see a card, we have no PCI obligations to fail. You can subscribe with confidence on day one."
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#EAB308] font-mono mb-3">// The arsenal</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase leading-tight">
            Every weapon you need<br />to win the drop.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard icon={Radar} title="Real-time scanner">
            Polls 15+ retailers across AU + US around the clock. Every TCG, every set, every sealed product. Sub-second alert dispatch when a target hits stock.
          </FeatureCard>
          <FeatureCard icon={Crosshair} title="Scalp mode" accent="#EF4444">
            Smart Action pipelines: trigger (restock, price drop, schedule) → filter (price, region, deal score) → action (auto-buy, 1-click cart, Discord ping). Fires in milliseconds the moment a target hits.
          </FeatureCard>
          <FeatureCard icon={Bell} title="Discord + Telegram alerts" accent="#5865F2">
            Push every match to your server channel or chat. Mark seen, clear, replay. Never miss a window.
          </FeatureCard>
          <FeatureCard icon={Tag} title="Deal-score engine" accent="#22C55E">
            Each listing gets a 0-100 score based on MSRP, recent prices, and stock pressure. Filter to only "hot deals".
          </FeatureCard>
          <FeatureCard icon={Globe} title="AU + US coverage">
            JB Hi-Fi, Kmart, Target AU, BIG W, EB Games, Zing, Game Traders + Target US, Walmart, Best Buy, Amazon, GameStop, TCGplayer, eBay, Pokemon Center.
          </FeatureCard>
          <FeatureCard icon={ShieldCheck} title="Encrypted vaults" accent="#3B82F6">
            Store credentials and payment cards locally with AES-128 (Fernet) encryption. Multi-account per retailer supported.
          </FeatureCard>
          <FeatureCard icon={Cpu} title="Watch rules">
            Save filter combos as watches: trigger only when in-stock + ≤ target price + deal ≥ N. No spam.
          </FeatureCard>
          <FeatureCard icon={MonitorDown} title="Windows .exe (required)" accent="#EF4444">
            The bot engine, store accounts, payments, shipping, and proxies all live in the desktop app, not the cloud. One-click installer. Free with your subscription.
          </FeatureCard>
          <FeatureCard icon={Sparkles} title="Made for TCG">
            Pokémon, MTG, Yu-Gi-Oh!, One Piece, Lorcana, Flesh and Blood. Every active set, every sealed product.
          </FeatureCard>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#0a0a0a] border-y border-[#262626] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#EAB308] font-mono mb-3">// Workflow</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase leading-tight">
              From zero to<br />first drop in 4 steps.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((s, i) => (
              <div
                key={s.step}
                className="bg-[#0d0d0d] border border-[#262626] p-6 rounded-sm relative overflow-hidden"
                style={{ animation: `slide-in-right 600ms ease-out ${i * 80}ms both` }}
              >
                <div className="font-mono text-5xl font-black text-[#262626] mb-4">{s.step}</div>
                <h3 className="font-heading text-base font-bold uppercase tracking-wider mb-2">{s.title}</h3>
                <p className="text-sm text-[#A3A3A3] leading-relaxed">{s.body}</p>
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EAB308] to-transparent opacity-30" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#EAB308] font-mono mb-3">// What hunters say</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase leading-tight">
            Resellers love it.<br />Collectors love it more.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-[#0d0d0d] border border-[#262626] p-6 rounded-sm relative">
              <Quote className="absolute top-4 right-4 w-6 h-6 text-[#262626]" />
              <p className="text-sm text-white leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 pt-4 border-t border-[#262626]">
                <div className="font-bold text-sm text-white">{t.name}</div>
                <div className="text-[11px] font-mono text-[#737373] uppercase tracking-wider">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[#0a0a0a] border-y border-[#262626] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#EAB308] font-mono mb-3">// Pricing</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase leading-tight mb-2">
            One plan. No tiers.<br />Cancel <span className="text-[#EAB308]">anytime</span>.
          </h2>
          <p className="text-sm text-[#A3A3A3] mb-10">
            30-day access pass. Renew when you want, don't when you don't. No subscriptions to track down to cancel.
          </p>

          <div className="bg-[#0d0d0d] border-2 border-[#EAB308] p-8 rounded-sm shadow-[0_0_40px_rgba(234,179,8,0.15)] text-left max-w-md mx-auto">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-heading text-2xl font-black uppercase tracking-tighter">CardRadar Pro</h3>
              <CreditCard className="w-5 h-5 text-[#EAB308]" />
            </div>
            <div className="flex items-baseline gap-2 mb-1 mt-4">
              <span className="font-mono text-5xl font-black text-white">${pricing?.currencies?.[0]?.amount?.toFixed(0) || "200"}</span>
              <span className="text-sm text-[#A3A3A3] font-mono">USD / 30 days</span>
            </div>
            <div className="text-[11px] font-mono text-[#737373]">
              or A${pricing?.currencies?.[1]?.amount?.toFixed(0) || "310"} AUD
            </div>

            <div className="space-y-2 mt-6 mb-6">
              {[
                "Unlimited watchlists + alerts",
                "All 15 retailers (AU + US)",
                "Scalp mode + encrypted vaults",
                "Discord + Telegram webhooks",
                "Windows .exe packaging",
                "Cancel anytime, just don't renew",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-[#D4D4D4]">
                  <Check className="w-4 h-4 text-[#22C55E] shrink-0" />
                  {f}
                </div>
              ))}
            </div>

            <Link
              to={ctaTarget}
              data-testid="pricing-cta"
              className="block w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-sm py-3 rounded-sm hover:bg-[#FDE047] transition-all text-center shadow-[0_0_18px_rgba(234,179,8,0.25)]"
            >
              {user ? (user.subscription_status === "active" ? "Open dashboard →" : "Subscribe now →") : "Start hunting →"}
            </Link>
            <p className="text-center text-[10px] font-mono text-[#737373] mt-3">
              Powered by Stripe · USD and AUD accepted
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#262626] py-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#737373]">
          CardRadar · TCG market scanner · Made for the hunt
        </div>
        <div className="font-mono text-[10px] text-[#525252] mt-2 max-w-2xl mx-auto">
          Auto-purchase features are provided as a framework for personal/local use. Real automated checkout violates retailer ToS and is the user's responsibility to implement.
        </div>
      </footer>
    </div>
  );
}
